import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "../components/ui/select";
import { Target, Play, BookOpen } from "lucide-react";

/**
 * Practice page — start a self-paced quiz. Users can now drill ANY
 * subtopic that has at least one question, grouped by parent topic in
 * the dropdown so the 95-subtopic list stays navigable.
 */
export default function Practice() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [topics, setTopics] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [topic, setTopic] = useState(params.get("topic") || "all");
  const [subtopic, setSubtopic] = useState(params.get("subtopic") || "any");
  const [count, setCount] = useState("10");

  useEffect(() => {
    api.get("/topics", { params: { lang } }).then(r => setTopics(r.data));
    api.get(`/coverage?lang=${lang}`).then(r => setCoverage(r.data));
  }, [lang]);

  // Reset subtopic when topic changes (otherwise filters get invalid pairs)
  useEffect(() => { setSubtopic("any"); }, [topic]);

  /**
   * Build dropdown groups: each topic_v2 with at least one subtopic
   * that has current > 0. We only surface subtopics that already carry
   * questions so the user never lands on an empty pool.
   */
  const subtopicGroups = useMemo(() => {
    if (!coverage) return [];
    return coverage.topics
      .map(t => ({
        ...t,
        subtopics: t.subtopics.filter(s => s.current > 0),
      }))
      .filter(t => t.subtopics.length > 0);
  }, [coverage]);

  const start = () => {
    const qs = new URLSearchParams({ mode: "practice", count });
    if (topic && topic !== "all") qs.set("topic", topic);
    if (subtopic && subtopic !== "any") qs.set("subtopic", subtopic);
    navigate(`/quiz?${qs.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 data-testid="practice-title" className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{t("practiceTitle")}</h1>
        <p className="text-gray-600 mb-8">{t("practiceSubtitle")}</p>

        <Card className="p-6 sm:p-8 bg-white border border-gray-200">
          <div className="grid sm:grid-cols-3 gap-5 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("selectTopic")}</label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger data-testid="topic-select" className="bg-gray-50">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTopics")}</SelectItem>
                  {topics.map(tp => (
                    <SelectItem key={tp.topic_id} value={tp.topic_id}>{tp.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("selectSubtopic")}</label>
              <Select value={subtopic} onValueChange={setSubtopic}>
                <SelectTrigger data-testid="subtopic-select" className="bg-gray-50">
                  <SelectValue placeholder={t("anySubtopic")}/>
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  <SelectItem value="any">{t("anySubtopic")}</SelectItem>
                  {subtopicGroups.map(grp => (
                    <SelectGroup key={grp.id}>
                      <SelectLabel className="text-[10px] uppercase tracking-wide text-gray-500 px-2 pt-2">
                        {grp.title}
                      </SelectLabel>
                      {grp.subtopics.map(s => (
                        <SelectItem
                          key={s.id}
                          value={s.id}
                          data-testid={`subtopic-opt-${s.id}`}
                        >
                          {s.title} <span className="text-gray-400">· {s.current}q</span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t("question")}s</label>
              <Select value={count} onValueChange={setCount}>
                <SelectTrigger data-testid="count-select" className="bg-gray-50">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">{t("select10")}</SelectItem>
                  <SelectItem value="20">{t("select20")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="start-practice" onClick={start} size="lg" className="bg-fjord hover:bg-fjord-700">
            <Play className="w-4 h-4 me-2"/> {t("startPractice")}
          </Button>
        </Card>

        {/* Top-coverage subtopics — quick-launch cards */}
        {coverage && (
          <>
            <h2 className="font-heading text-xl font-semibold mt-12 mb-2 flex items-baseline gap-2">
              {t("popularSubtopics")}
              <span className="text-xs text-gray-500 font-normal">{t("popularSubtopicsHint")}</span>
            </h2>
            <div data-testid="popular-subtopics" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger mb-12">
              {subtopicGroups
                .flatMap(g => g.subtopics.map(s => ({...s, parentTitle: g.title})))
                .sort((a, b) => b.current - a.current)
                .slice(0, 9)
                .map(s => (
                  <Card
                    key={s.id}
                    data-testid={`quick-subtopic-${s.id}`}
                    onClick={() => navigate(`/quiz?mode=practice&count=10&subtopic=${s.id}`)}
                    className="p-4 bg-white border border-gray-200 cursor-pointer hover:border-fjord/40 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-md bg-fjord/10 text-fjord grid place-items-center shrink-0">
                        <BookOpen className="w-4 h-4"/>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                          {s.parentTitle}
                        </div>
                        <div className="font-semibold text-gray-900 text-sm leading-snug">{s.title}</div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="outline" className="text-[10px]">{s.current}q ready</Badge>
                          {s.gap > 0 && (
                            <span className="text-[10px] text-gray-400">{s.gap} more coming</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
            </div>
          </>
        )}

        <h2 className="font-heading text-xl font-semibold mt-12 mb-4">{t("topics")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {topics.map(tp => (
            <Card key={tp.topic_id} data-testid={`quick-topic-${tp.topic_id}`}
              onClick={()=>navigate(`/quiz?mode=practice&count=10&topic=${tp.topic_id}`)}
              className="p-5 bg-white border border-gray-200 cursor-pointer hover:border-fjord/40 hover:shadow-md transition-all">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-fjord/10 text-fjord grid place-items-center shrink-0">
                  <Target className="w-4 h-4"/>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{tp.title}</div>
                  <div className="text-xs text-gray-500">{tp.lesson_count} {t("lessons")}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
